import React, { useEffect, useRef, useState } from 'react';
import '../../styles/sections/services/ServiceCardsCarouselSection.css';

// Simple card presentation with background image and overlay text
const ServiceCard = ({ title, subtitle, image, className = '' }) => {
  const renderParagraphs = (text) => {
    if (!text) return null;
    // Prefer explicit double line breaks if provided
    let blocks = String(text).split(/\n\n+/);
    if (blocks.length === 1) {
      // Fallback: group sentences into paragraphs (2 sentences per paragraph)
      const sentences = String(text).split(/(?<=\.)\s+/);
      const grouped = [];
      for (let i = 0; i < sentences.length; i += 2) {
        grouped.push(sentences.slice(i, i + 2).join(' '));
      }
      blocks = grouped;
    }
    return blocks.filter(Boolean).map((block, idx) => (
      <p key={idx} className="service-card__subtitle">{block}</p>
    ));
  };

  return (
    <article className={`service-card ${className}`} style={{ backgroundImage: `url(${image})` }}>
      <div className="service-card__overlay" />
      <div className="service-card__content">
        <h3 className="service-card__title">{title}</h3>
        {renderParagraphs(subtitle)}
      </div>
    </article>
  );
};

const ServiceCardsCarouselSection = ({
  title = 'NUESTROS SERVICIOS',
  subtitle = 'Conocé en todo lo que te podemos ayudar',
  cards = [],
  className = '',
  animationDuration = 60,
  pauseOnHover = true
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startTranslate, setStartTranslate] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const speedRef = useRef(0.3);
  // Refs para valores que cambian frecuentemente en touch events
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startTranslateRef = useRef(0);

  // Ensure we always render 6 cards; if fewer provided, repeat
  const normalizedCards = (() => {
    if (cards.length >= 6) return cards.slice(0, 6);
    const base = [...cards];
    while (base.length < 6) base.push(cards[base.length % Math.max(1, cards.length)] || {});
    return base.slice(0, 6);
  })();

  // Duplicar cards para crear el efecto de loop infinito
  const duplicatedCards = [...normalizedCards, ...normalizedCards];

  // Función para obtener el ancho de un set de cards (la mitad del track)
  const getSingleSetWidth = () => {
    if (!trackRef.current) return 0;
    return trackRef.current.scrollWidth / 2;
  };

  // Calcular velocidad basada en animationDuration
  const calculateSpeed = () => {
    if (!trackRef.current) return 0.3;
    const singleSetWidth = getSingleSetWidth();
    if (singleSetWidth === 0) return 0.3;
    const fps = 60;
    const totalFrames = animationDuration * fps;
    return singleSetWidth / totalFrames;
  };

  // Actualizar velocidad cuando cambia el ancho del track o animationDuration
  useEffect(() => {
    if (trackRef.current) {
      speedRef.current = calculateSpeed();
    }
  }, [animationDuration, normalizedCards.length]);

  // Animación automática usando requestAnimationFrame
  useEffect(() => {
    if (isDragging || isPaused || !trackRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const animate = () => {
      if (!trackRef.current || isDragging || isPaused) return;
      
      setCurrentTranslate((prev) => {
        const singleSetWidth = getSingleSetWidth();
        if (singleSetWidth === 0) return prev;
        
        speedRef.current = calculateSpeed();
        const newPosition = prev - speedRef.current;
        if (newPosition <= -singleSetWidth) {
          return 0;
        }
        return newPosition;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, isPaused, animationDuration]);

  // Aplicar el transform al track
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${currentTranslate}px)`;
    }
  }, [currentTranslate]);

  const handleMouseDown = (e) => {
    if (!containerRef.current || !trackRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    isDraggingRef.current = true;
    setStartX(e.pageX);
    startXRef.current = e.pageX;
    const transform = trackRef.current.style.transform;
    const match = transform.match(/translateX\(([^)]+)\)/);
    const currentPos = match ? parseFloat(match[1]) : currentTranslate;
    setStartTranslate(currentPos);
    startTranslateRef.current = currentPos;
    setCurrentTranslate(currentPos);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !trackRef.current) return;
    e.preventDefault();
    const deltaX = (e.pageX - startX) * 0.8;
    const newPosition = startTranslate + deltaX;
    const singleSetWidth = getSingleSetWidth();
    
    if (singleSetWidth === 0) {
      setCurrentTranslate(newPosition);
      return;
    }
    
    let normalized = newPosition;
    if (normalized <= -singleSetWidth) {
      normalized = normalized + singleSetWidth;
    } else if (normalized > 0) {
      normalized = normalized - singleSetWidth;
    }
    setCurrentTranslate(normalized);
  };

  const stopDrag = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setTimeout(() => {
      setIsPaused(false);
    }, 100);
  };

  const handleTouchStart = (e) => {
    if (!containerRef.current || !trackRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    isDraggingRef.current = true;
    const touchX = e.touches[0].pageX;
    setStartX(touchX);
    startXRef.current = touchX;
    const transform = trackRef.current.style.transform;
    const match = transform.match(/translateX\(([^)]+)\)/);
    const currentPos = match ? parseFloat(match[1]) : currentTranslate;
    setStartTranslate(currentPos);
    startTranslateRef.current = currentPos;
    setCurrentTranslate(currentPos);
  };

  // Manejar todos los eventos touch con passive:false para poder usar preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onTouchMove = (e) => {
      if (!isDraggingRef.current || !trackRef.current) return;
      e.preventDefault();
      const deltaX = (e.touches[0].pageX - startXRef.current) * 0.8;
      const newPosition = startTranslateRef.current + deltaX;
      const singleSetWidth = getSingleSetWidth();
      
      if (singleSetWidth === 0) {
        setCurrentTranslate(newPosition);
        return;
      }
      
      let normalized = newPosition;
      if (normalized <= -singleSetWidth) {
        normalized = normalized + singleSetWidth;
      } else if (normalized > 0) {
        normalized = normalized - singleSetWidth;
      }
      setCurrentTranslate(normalized);
    };

    const onTouchEnd = () => {
      stopDrag();
    };

    // Registrar con passive:false para poder usar preventDefault
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []); // Sin dependencias - usa refs para valores que cambian

  // CSS maneja la igualación de alturas automáticamente con align-items: stretch

  return (
    <section className={`services-cards-carousel-section ${className}`}>
      <div className="container">
        <div className="services-cards-carousel-section__header">
          <h2 className="section-title">{title}</h2>
          {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>

        <div
          ref={containerRef}
          className={`services-cards-carousel-section__slider ${pauseOnHover ? 'services-cards-carousel-section__slider--pause-hover' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onTouchStart={handleTouchStart}
          // Touch events se manejan con event listeners nativos para poder usar passive:false
          onMouseEnter={() => pauseOnHover && !isDragging && setIsPaused(true)}
          onMouseLeave={(e) => {
            stopDrag();
            if (pauseOnHover && !isDragging) {
              setIsPaused(false);
            }
          }}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div
            ref={trackRef}
            className="services-cards-carousel-section__track"
          >
            {duplicatedCards.map((card, idx) => (
              <div key={`service-card-${idx}`} className="services-cards-carousel-section__slide">
                <ServiceCard 
                  title={card.title}
                  subtitle={card.subtitle}
                  image={card.image}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCardsCarouselSection;


